/**
 * Утилиты для работы с продуктами в корзине
 */

/**
 * Интерфейс добавки
 */
export interface ProductAddon {
  id: string
  name: string
  description?: string
  price: string | number
  img?: string
}

/**
 * Интерфейс исключения
 */
export interface ProductExclude {
  id: string
  name: string
  img?: string | null
  required?: boolean
  max_quantity?: number
}

/**
 * Получает массив ID из массива объектов или строк
 * @param items - массив элементов
 * @returns отсортированный массив ID
 */
export function extractIds(items: any[]): string[] {
  if (!Array.isArray(items)) return []
  
  return items
    .map(item => {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        return String(item.id)
      }
      return String(item)
    })
    .sort()
}

/**
 * Находит добавки по их ID
 * @param addonIds - массив ID добавок (строки или объекты)
 * @param availableAddons - доступные добавки
 * @returns массив найденных добавок
 */
export function findAddonsByIds(
  addonIds: any[], 
  availableAddons: ProductAddon[],
): ProductAddon[] {
  if (!Array.isArray(addonIds)) return []
  
  // Если уже объекты - возвращаем как есть
  if (addonIds.length > 0 && typeof addonIds[0] === 'object') {
    return addonIds as ProductAddon[]
  }
  
  // Ищем по ID
  return addonIds
    .map((id: string | number) =>
      availableAddons.find((addon) => String(addon.id) === String(id)),
    )
    .filter((addon): addon is ProductAddon => !!addon)
}

/**
 * Находит исключения по их ID
 * @param excludeIds - массив ID исключений (строки или объекты)
 * @param availableExcludes - доступные исключения
 * @returns массив найденных исключений
 */
export function findExcludesByIds(
  excludeIds: any[], 
  availableExcludes: ProductExclude[],
): ProductExclude[] {
  if (!Array.isArray(excludeIds)) return []
  
  // Если уже объекты - возвращаем как есть
  if (excludeIds.length > 0 && typeof excludeIds[0] === 'object') {
    return excludeIds as ProductExclude[]
  }
  
  // Ищем по ID
  return excludeIds
    .map((id: string | number) =>
      availableExcludes.find((exclude) => String(exclude.id) === String(id)),
    )
    .filter((exclude): exclude is ProductExclude => !!exclude)
}

/**
 * Форматирует цену добавки
 * @param price - цена (строка или число)
 * @returns отформатированная строка цены
 */
export function formatAddonPrice(price: string | number): string {
  const numPrice = typeof price === 'string' ? Number.parseFloat(price) : price
  return Number.isNaN(numPrice) ? '0' : Math.round(numPrice).toString()
}

/**
 * Вычисляет общую стоимость добавок
 * @param addons - массив добавок
 * @returns общая стоимость
 */
export function calculateAddonsTotal(addons: ProductAddon[]): number {
  return addons.reduce((total, addon) => {
    const price = typeof addon.price === 'string' ? Number.parseFloat(addon.price) : addon.price
    return total + (Number.isNaN(price) ? 0 : price)
  }, 0)
}

/**
 * Проверяет, есть ли у продукта изображение
 * @param images - массив изображений продукта
 * @returns true если есть хотя бы одно изображение
 */
export function hasProductImage(images: any[]): boolean {
  return Array.isArray(images) && images.length > 0
}

/**
 * Получает первое изображение продукта
 * @param images - массив изображений
 * @returns объект изображения или null
 */
export function getFirstProductImage(images: any[]): any | null {
  if (!hasProductImage(images)) return null
  return images[0]
}

/**
 * Создает объект для отображения информации о продукте
 * @param product - объект продукта
 * @param addons - найденные добавки
 * @param excludes - найденные исключения
 * @returns объект для UI
 */
export function createProductDisplayInfo(
  product: any,
  addons: ProductAddon[],
  excludes: ProductExclude[],
) {
  return {
    name: product.name || 'Позиция',
    price: product.price || 0,
    quantity: product.quantity || 1,
    image: getFirstProductImage(product.img),
    addons: addons.map(addon => ({
      name: addon.name,
      price: formatAddonPrice(addon.price),
    })),
    excludes: excludes.map(exclude => ({
      name: exclude.name,
    })),
    hasAddons: addons.length > 0,
    hasExcludes: excludes.length > 0,
    addonsTotal: calculateAddonsTotal(addons),
  }
}

/**
 * Валидирует данные продукта
 * @param product - объект продукта
 * @returns true если данные валидны
 */
export function validateProductData(product: any): boolean {
  return product && 
         typeof product === 'object' &&
         product.id &&
         product.name &&
         typeof product.price === 'number' &&
         product.price >= 0
}